/**
 * MandateSetup — Multi-step onboarding wizard (3 steps).
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMandateStore } from '../../stores/mandate/mandateStore';
import { reflectOnHypothesis } from '../../lib/llmMandateService';
import type { STaRSSituation } from '../../types/mandate';

const SITUATION_OPTIONS: {
  type: STaRSSituation;
  label: string;
  description: string;
  detail: string;
}[] = [
  {
    type: 'startup',
    label: 'Startup',
    description: 'Building something new',
    detail: 'Greenfield initiative — no existing team or established process to inherit.',
  },
  {
    type: 'turnaround',
    label: 'Turnaround',
    description: 'Fixing something broken',
    detail: 'Performance problems and urgency to change course quickly.',
  },
  {
    type: 'realignment',
    label: 'Realignment',
    description: 'Redirecting something that works',
    detail: 'Success without disruption — the org performs but needs new strategic direction.',
  },
  {
    type: 'sustaining',
    label: 'Sustaining Success',
    description: 'Growing something strong',
    detail: 'High-performing team and systems — your job is to keep them strong.',
  },
];

function getTodayStr(): string {
  return new Date().toISOString().split('T')[0];
}

function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
}

export default function MandateSetup() {
  const navigate = useNavigate();
  const { createTransition, setOutcomeHypothesis } = useMandateStore();

  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  // Step 1
  const [situationType, setSituationType] = useState<STaRSSituation | null>(null);
  const [situationNotes, setSituationNotes] = useState('');

  // Step 2
  const today = getTodayStr();
  const [role, setRole] = useState('');
  const [organization, setOrganization] = useState('');
  const [title, setTitle] = useState('');
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(addDays(today, 100));

  // Step 3
  const [hypothesis, setHypothesis] = useState('');
  const [reflection, setReflection] = useState<string | null>(null);
  const [reflecting, setReflecting] = useState(false);
  const reflectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const canProceedStep1 = situationType !== null;
  const canProceedStep2 = role.trim().length > 0 && startDate.length > 0;
  const canProceedStep3 = hypothesis.trim().length > 0;

  // Debounced AI reflection — fires 1.5s after the user stops typing
  useEffect(() => {
    setReflection(null);
    if (reflectTimer.current) clearTimeout(reflectTimer.current);
    if (hypothesis.trim().length < 40) return;

    setReflecting(true);
    reflectTimer.current = setTimeout(async () => {
      try {
        const result = await reflectOnHypothesis(hypothesis, situationType);
        setReflection(result);
      } finally {
        setReflecting(false);
      }
    }, 1500);

    return () => {
      if (reflectTimer.current) clearTimeout(reflectTimer.current);
    };
  }, [hypothesis, situationType]);

  async function handleFinish() {
    if (!canProceedStep3 || submitting) return;
    setSubmitting(true);
    try {
      const resolvedTitle = title.trim() || `${role.trim()} @ ${organization.trim()}`;
      const transition = await createTransition({
        title: resolvedTitle,
        organization: organization.trim(),
        role: role.trim(),
        startDate,
        endDate,
        situationType: situationType ?? undefined,
        situationNotes: situationNotes.trim() || undefined,
        status: 'active',
      });
      if (transition) {
        await setOutcomeHypothesis(hypothesis.trim());
        navigate('/apps/mandate/dashboard');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Progress indicator */}
      <div className="flex items-center gap-2 mb-8">
        {[1, 2, 3].map((s) => (
          <div key={s} className="flex items-center gap-2">
            <div
              className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold ${
                s === step
                  ? 'bg-primary-600 text-white'
                  : s < step
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-[var(--hover-tint)] text-[var(--text-ter)]'
              }`}
            >
              {s}
            </div>
            {s < 3 && <div className={`w-8 h-0.5 ${s < step ? 'bg-primary-300' : 'bg-[var(--hover-tint)]'}`} />}
          </div>
        ))}
        <span className="ml-2 text-sm text-[var(--text-ter)]">
          {step === 1 && 'Your Situation'}
          {step === 2 && 'Your Role'}
          {step === 3 && 'Day 100 Hypothesis'}
        </span>
      </div>

      {/* Step 1: Situation */}
      {step === 1 && (
        <div>
          <h1 className="text-xl font-semibold text-[var(--text-pri)] mb-1">What kind of transition is this?</h1>
          <p className="text-sm text-[var(--text-ter)] mb-6">
            The STaRS framework helps you orient your first 100 days to what the situation demands.
          </p>

          <div className="grid grid-cols-2 gap-3 mb-6">
            {SITUATION_OPTIONS.map((opt) => (
              <button
                key={opt.type}
                onClick={() => setSituationType(opt.type)}
                className={`text-left p-4  border-2 transition-all ${
                  situationType === opt.type
                    ? 'border-primary-500 bg-primary-50'
                    : 'border border-[var(--rule)] bg-[var(--bg-raised)] hover:border-[var(--rule)]'
                }`}
              >
                <div className="font-semibold text-[var(--text-pri)] text-sm mb-0.5">{opt.label}</div>
                <div className="text-xs text-[var(--text-sec)] font-medium mb-1">{opt.description}</div>
                <div className="text-xs text-[var(--text-ter)] leading-relaxed">{opt.detail}</div>
              </button>
            ))}
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Situation notes <span className="text-[var(--text-ter)] font-normal">(optional)</span>
            </label>
            <textarea
              value={situationNotes}
              onChange={(e) => setSituationNotes(e.target.value)}
              rows={3}
              className="w-full border border-[var(--rule)] px-3 py-2 text-sm text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
              placeholder="What do you know about this situation so far?"
            />
          </div>

          <button
            onClick={() => setStep(2)}
            disabled={!canProceedStep1}
            className="bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white font-medium px-4 py-2"
          >
            Next →
          </button>
        </div>
      )}

      {/* Step 2: Role */}
      {step === 2 && (
        <div>
          <h1 className="text-xl font-semibold text-[var(--text-pri)] mb-1">Your role</h1>
          <p className="text-sm text-[var(--text-ter)] mb-6">Tell us about the role you're stepping into.</p>

          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                Your role / title <span className="text-[var(--coral)]">*</span>
              </label>
              <input
                type="text"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="w-full border border-[var(--rule)] px-3 py-2 text-sm text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="e.g. VP of Product"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Organization</label>
              <input
                type="text"
                value={organization}
                onChange={(e) => setOrganization(e.target.value)}
                className="w-full border border-[var(--rule)] px-3 py-2 text-sm text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="e.g. Acme Corp"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                Transition name{' '}
                <span className="text-[var(--text-ter)] font-normal">
                  (optional — defaults to role + org)
                </span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full border border-[var(--rule)] px-3 py-2 text-sm text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder={role && organization ? `${role} @ ${organization}` : 'e.g. VP Product transition at Acme'}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                  Start date <span className="text-[var(--coral)]">*</span>
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setEndDate(addDays(e.target.value, 100));
                  }}
                  className="w-full border border-[var(--rule)] px-3 py-2 text-sm text-[var(--text-pri)] focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                  Day 100 (end date)
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full border border-[var(--rule)] px-3 py-2 text-sm text-[var(--text-pri)] focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setStep(1)}
              className="text-sm text-[var(--text-sec)] hover:text-[var(--text-pri)] font-medium px-4 py-2 border border-[var(--rule)] hover:bg-[var(--surface)]"
            >
              ← Back
            </button>
            <button
              onClick={() => setStep(3)}
              disabled={!canProceedStep2}
              className="bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white font-medium px-4 py-2"
            >
              Next →
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Day 100 Hypothesis */}
      {step === 3 && (
        <div>
          <h1 className="text-xl font-semibold text-[var(--text-pri)] mb-1">
            What does success look like on Day 100?
          </h1>
          <p className="text-sm text-[var(--text-sec)] mb-1 leading-relaxed">
            Describe what you want to be able to show your boss on Day 100 — not what you'll have
            accomplished, but what will be <em>different</em> about this organization because of how
            you showed up.
          </p>

          <div className="mb-2 mt-5">
            <textarea
              value={hypothesis}
              onChange={(e) => setHypothesis(e.target.value)}
              rows={8}
              className="w-full border border-[var(--rule)] px-3 py-3 text-sm text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none min-h-[200px]"
              placeholder="If you were sitting across from your boss on Day 100 and they asked whether you were the right person for this role — what would you want to be able to show them?"
            />
          </div>

          {/* AI Reflection */}
          {(reflecting || reflection) && (
            <div
              className={`mb-4  border px-4 py-3 transition-all ${
                reflecting ? 'border-[var(--rule)] bg-[var(--surface)]' : 'border-[var(--slate)] bg-[var(--slate-dim)]'
              }`}
            >
              {reflecting ? (
                <p className="text-sm text-[var(--text-ter)] italic">Reflecting…</p>
              ) : (
                <p className="text-sm text-[var(--text-ter)] leading-relaxed">{reflection}</p>
              )}
            </div>
          )}

          <p className="text-xs text-[var(--text-ter)] mb-6">
            This is a hypothesis. You can revise it as you learn more — you'll be asked to name
            what changed when you do.
          </p>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setStep(2)}
              className="text-sm text-[var(--text-sec)] hover:text-[var(--text-pri)] font-medium px-4 py-2 border border-[var(--rule)] hover:bg-[var(--surface)]"
            >
              ← Back
            </button>
            <button
              onClick={handleFinish}
              disabled={!canProceedStep3 || submitting}
              className="bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white font-medium px-4 py-2"
            >
              {submitting ? 'Starting...' : 'Start my transition →'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
